// src/pages/Character.js
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppConfig } from '../ConfigContext';
import { usePostHog } from "posthog-js/react";
import '../index.css';
import { getSkinSrc } from "../components/SeasonalSkins";
import LoadingSpinner from "../components/LoadingSpinner";

const ABILITY_CHANGE_HINT_ENABLED = true;
const ABILITY_HINT_STORAGE_KEY = "ability_change_hint_seen_v1";

const API_BASE = process.env.REACT_APP_API_URL;

const normalizeItemType = (t) => {
  const s = String(t ?? "").trim().toLowerCase();

  if (
    s === "cardpieces" ||
    s === "cardpiece" ||
    s === "card_pieces" ||
    s === "card-pieces"
  ) {
    return "cardPieces";
  }

  return t;
};

/* ================== Shared game data (exported) ================== */
export const abilities_desc = [
  { id: 'execution',   desc: '+3 dmg if enemy ≤15% HP.' },
  { id: 'aegis',       desc: 'Blocks next hit (1 charge on first turn).' },
  { id: 'berserk',     desc: '+2 ATK after first hit.' },
  { id: 'flameburst',  desc: 'Burns 2 turns (2 dmg/turn).' },
  { id: 'fortify',     desc: '+5 Max HP at start.' },
  { id: 'pierce',      desc: 'Ignores 30% of enemy DEF.' },
  { id: 'frenzy',      desc: '15% to strike twice (2nd hits 60%).' },
  { id: 'shield wall', desc: '+3 DEF at start.' },
  { id: 'thorns',      desc: 'Reflects 15% dmg taken (cap 15% max HP).' },
  { id: 'bleed',       desc: 'Bleeds 3 turns (2 dmg/turn).' },
  { id: 'purify',      desc: 'Start of turn: clears one status.' },
  { id: 'regen',       desc: 'Once per battle: heal 2 HP (1 if hurt).' },
];

export const ABILITIES = [
  'Berserk', 'Pierce', 'Frenzy', 'Bleed', 'Flameburst', 'Execution',
  'Shield Wall', 'Regen', 'Thorns', 'Aegis', 'Purify', 'Fortify'
];

const MAX_LEVEL = 10;

export const RARITY_PROFILES = {
  Common:    { atk: [2, 5],  def: [1, 4],  hp: [14, 22], abilities: [0, 1] },
  Rare:      { atk: [3, 6],  def: [2, 5],  hp: [18, 28], abilities: [1, 1] },
  Epic:      { atk: [5, 8],  def: [3, 7],  hp: [24, 36], abilities: [1, 2] },
  Legendary: { atk: [7, 10], def: [5, 9],  hp: [30, 44], abilities: [2, 3] }
};

const MAX_CARD_NAME_LEN = 21;

const truncateCardName = (name) => {
  const n = String(name || '');
  if (n.length <= MAX_CARD_NAME_LEN) return n;
  return n.slice(0, MAX_CARD_NAME_LEN - 1) + "…";
};

const calcEntryFee = (card, shop) => {
  if (!card) return 0;
  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  };
  const rarity = String(card.rarity || 'Common').toLowerCase();
  const feeByRarity = {
    common:    toNum(shop?.fightCost?.common ?? shop?.fight?.common),
    rare:      toNum(shop?.fightCost?.rare   ?? shop?.fight?.rare),
    epic:      toNum(shop?.fightCost?.epic   ?? shop?.fight?.epic),
    legendary: toNum(shop?.fightCost?.legendary ?? shop?.fight?.legendary),
  };
  return feeByRarity[rarity] ?? 0;
};

function normalizeShop(raw) {
  // ...existing packs/runes/sparks/evolve/fight...
  const rewardPercent = Number(raw?.rewardPercent ?? raw?.REWARD_PERCENT ?? raw?.fight?.rewardPercent ?? 0);
  const fightCost = {
    common: Number(raw?.fightCost?.common ?? raw?.fight?.common ?? 0),
    rare: Number(raw?.fightCost?.rare ?? raw?.fight?.rare ?? 0),
    epic: Number(raw?.fightCost?.epic ?? raw?.fight?.epic ?? 0),
    legendary: Number(raw?.fightCost?.legendary ?? raw?.fight?.legendary ?? 0),
  };
  return { packs, runes, sparks, evolve, fight, rewardPercent, fightCost };
}

// --- Inline hint button (local to this file) ---
const OUT_OF_SPARKS_TEXT = 'Not enough Sparks';

function SparkHint({ show, text = OUT_OF_SPARKS_TEXT }) {
  return (
    <AnimatePresence initial={false}>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 6, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6, scale: 0.98, filter: 'blur(2px)' }}
          transition={{ duration: 0.22, ease: 'easeOut' }}
          style={{
            position: 'absolute',
            left: '50%',
            bottom: '100%',
            transform: 'translate(-50%, -8px)',
            padding: '6px 10px',
            borderRadius: 10,
            fontSize: 13,
            color: '#fff',
            background: 'rgba(0,0,0,0.75)',
            border: '1px solid rgba(255,255,255,0.25)',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 20,
          }}
          aria-live="polite"
        >
          {text}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const SparkGuardButton = React.forwardRef(function SparkGuardButton(
  { user, onConfirm, children, hintText = OUT_OF_SPARKS_TEXT, style, ...props },
  ref
) {
  const [showHint, setShowHint] = React.useState(false);
  const timerRef = React.useRef(null);
  const buttonRef = React.useRef(null);

  const getUserSparks = (u) => Number(u?.sparks ?? u?.credits ?? u?.balance ?? 0);
  const hasSparks = Number.isFinite(getUserSparks(user)) ? getUserSparks(user) > 0 : true;

  const revealHint = (ms = 1400) => {
    setShowHint(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setShowHint(false), ms);
  };

  React.useImperativeHandle(ref, () => ({
    focus: () => buttonRef.current?.focus(),
    showHint: (ms) => revealHint(ms),
  }));

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (hasSparks) {
      onConfirm?.();
      return;
    }
    revealHint();
  };

  React.useEffect(() => () => clearTimeout(timerRef.current), []);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <SparkHint show={showHint} text={hintText} />
      <button
        ref={buttonRef}
        onClick={handleClick}
        aria-disabled={!hasSparks}
        style={{
          minWidth: 120,
          opacity: hasSparks ? 1 : 0.7,
          cursor: hasSparks ? 'pointer' : 'not-allowed',
          ...style,
        }}
        {...props}
      >
        {children}
      </button>
    </div>
  );
});


/* ================== Component ================== */
function Character({
  user,
  setUser,
  selectedCardId,
  setSelectedCardId,
  onNavigate,
  openedVia = 'menu',
  closeCharacterModal,
}) {
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState([]);
  const posthog = usePostHog();

const [limitInfo, setLimitInfo] = useState({
  loading: true,
  used: 0,
  limit: 3,
  resetAt: null,
});

const [cooldownLabel, setCooldownLabel] = useState(null);

  const enterBtnRef = useRef(null);

  // evolve
  // evolve
const [evolveOpen, setEvolveOpen] = useState(false);
const [evolveData, setEvolveData] = useState(null);
const [evolvingId, setEvolvingId] = useState(null);
const [evolveConfirmOpen, setEvolveConfirmOpen] = useState(false);
const [evolveConfirmCard, setEvolveConfirmCard] = useState(null);

const [pieceConvertOpen, setPieceConvertOpen] = useState(false);
const [pieceConvertCard, setPieceConvertCard] = useState(null);
const [pieceConvertBusy, setPieceConvertBusy] = useState(false);
const [pieceConvertSuccess, setPieceConvertSuccess] = useState(null);

// Inventory pieces shown only inside evolve modal
const [invItems, setInvItems] = useState([]);
const [inventoryLoaded, setInventoryLoaded] = useState(false);
const [inventoryLoading, setInventoryLoading] = useState(false);
const [pieceClaimBusyId, setPieceClaimBusyId] = useState(null);
const [pieceClaimError, setPieceClaimError] = useState("");

  // errors
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [defenseDeckRequiredOpen, setDefenseDeckRequiredOpen] = useState(false);

  // marketplace sell
  const [sellOpen, setSellOpen] = useState(false);
  const [sellPrice, setSellPrice] = useState('');
  const [sellingCard, setSellingCard] = useState(null);
  const [sellingBusy, setSellingBusy] = useState(false);

  // battle modal (same behavior as Fight.js)
  const [battleSoonOpen, setBattleSoonOpen] = useState(false);
  const [pendingCard, setPendingCard] = useState(null);
  const [entering, setEntering] = useState(false);
  const [entryFee, setEntryFee] = useState(0);
  const [feeLoading, setFeeLoading] = useState(true);

  const { shop, evolvements } = useAppConfig();

  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameBusy, setRenameBusy] = useState(false);

  const [showAbilityHint, setShowAbilityHint] = useState(false);
  const [hintAbilityIndex, setHintAbilityIndex] = useState(0);

  const dismissAbilityHintForever = () => {
  localStorage.setItem(ABILITY_HINT_STORAGE_KEY, "true");
  setShowAbilityHint(false);
  };

  const [abilityChangeOpen, setAbilityChangeOpen] = useState(false);
  const [abilityChangeBusy, setAbilityChangeBusy] = useState(false);
  const [abilityTarget, setAbilityTarget] = useState(null);
  const [abilityChangeInfo, setAbilityChangeInfo] = useState({
    loading: true,
    enabled: false,
    used: 0,
    limit: 3,
    limitMode: "overall",
    nextPrice: 0,
    basePrice: 0,
    payCurrency: null,
    resetAt: null,
    refillPriceRunes: 0,
    canRefill: false,
  });

  const [abilityRefillOpen, setAbilityRefillOpen] = useState(false);
  const [abilityRefillBusy, setAbilityRefillBusy] = useState(false);

  const nameChangePrice = useMemo(() => {
    const raw =
      shop?.nameChangePrice ??
      shop?.renamePrice ??
      shop?.cardNamePrice ??
      shop?.cardRenamePrice ??
      shop?.NAME_CHANGE_PRICE ??
      0;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }, [shop]);

const abilityChangeConfig = useMemo(() => {
  const runes = Number(shop?.abilityChangePriceRunes ?? shop?.ABILITY_CHANGE_PRICE_RUNES ?? 0);
  const sparks = Number(shop?.abilityChangePriceSparks ?? shop?.ABILITY_CHANGE_PRICE_SPARKS ?? 0);

  return {
    runes: Number.isFinite(runes) && runes >= 0 ? runes : 0,
    sparks: Number.isFinite(sparks) && sparks >= 0 ? sparks : 0,
  };
}, [shop]);

const abilityChangeCurrency =
  abilityChangeInfo.payCurrency ||
  (abilityChangeConfig.runes > 0 ? 'runes' :
   abilityChangeConfig.sparks > 0 ? 'sparks' :
   null);

const abilityChangePrice =
  abilityChangeInfo.nextPrice ??
  (abilityChangeCurrency === 'runes'
    ? abilityChangeConfig.runes
    : abilityChangeConfig.sparks);

const abilityChangeEnabled = !!abilityChangeCurrency && abilityChangePrice > 0;
const selectedCard = cards[0];

  /* ---------- RNG flavor for modal (same set as Fight.js) ---------- */
  const allBattleTaglines = [
    'Shuffle well. Fate hates sloppy decks. 🃏',
    'RNGesus is watching… and probably laughing.',
    'May your draws be blessed and your topdecks cruel.',
    'Time to turn cardboard into chaos!',
    'Stack your luck, play your bluff.',
    'One misplay away from greatness!',
    'Draw. Play. Pray. Repeat.',
    'Big brains, small cards, massive egos.',
    'Mana? Never enough. Confidence? Always too much.',
    'Legends are written in crits and misclicks.',
    'Lose gracefully, or win obnoxiously — your call.',
    'Every hand’s a new bad decision waiting to happen.',
    'If you believe in the heart of the cards, blink twice.',
    'Skill matters… right after luck.',
    'Prepare to deck-stroy your enemies!',
    'It’s not RNG — it’s “strategic unpredictability.”',
    'Your deck’s ready. Your dignity? Questionable.',
    'Card advantage is temporary. Style is forever.',
    'Draw 5, regret 4, play 1.',
    'Welcome to the shuffle zone — no refunds.',
  ];
  const battleTaglines = allBattleTaglines.filter(line => line.length <= 42);
  const battleBullets = [
    'Draw wisely. Mulligan recklessly. 😎',
    'The real meta is emotional damage.',
    'Every deck tells a story… most end in tears.',
    'RNG is just skill wearing a disguise.',
    'Big brain plays, tiny cardboard soldiers.',
    'Tilt is free. Use it responsibly.',
    'Legends aren’t drawn — they’re topdecked.',
    'Outplay them. Outluck them. Out-meme them.',
    'Deck out? Blackout. Same thing.',
    'Remember: your opponent also thinks they’re the main character.',
  ];
  const [tagline, setTagline] = useState(battleTaglines[0]);
  const [bulletLines, setBulletLines] = useState(battleBullets);

  // NEW: fetch battle limit as soon as this character is selected
useEffect(() => {
  let cancelled = false;
  (async () => {
    if (!selectedCardId) return;

    setLimitInfo({ loading: true, used: 0, limit: 10, resetAt: null });

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/battle/limit?cardId=${encodeURIComponent(
          selectedCardId
        )}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      const data = await res.json().catch(() => ({}));
      if (cancelled) return;

      if (res.ok && Number.isFinite(Number(data?.limit))) {
        setLimitInfo({
          loading: false,
          used: Number(data.used ?? 0),
          limit: Number(data.limit ?? 10),
          resetAt: data.resetAt || null,
        });
      } else {
        setLimitInfo((i) => ({ ...i, loading: false }));
      }
    } catch {
      if (!cancelled) setLimitInfo((i) => ({ ...i, loading: false }));
    }
  })();

  return () => {
    cancelled = true;
  };
}, [selectedCardId]);

const openAbilityChange = (ability) => {
  if (!selectedCard) return;
  if (!abilityChangeEnabled) return;

  if (
    !abilityChangeInfo.loading &&
    abilityChangeInfo.limit > 0 &&
    abilityChangeInfo.used >= abilityChangeInfo.limit
  ) {
    if (Number(abilityChangeInfo.refillPriceRunes ?? 0) > 0) {
      setAbilityTarget(ability);
      setAbilityRefillOpen(true);
      return;
    }

    setErrorMessage(
      abilityChangeInfo.limitMode === "overall"
        ? "Limit reached for ability changes."
        : "Daily limit reached for ability changes."
    );
    setShowErrorModal(true);
    return;
  }

  setAbilityTarget(ability);
  setAbilityChangeOpen(true);
};

useEffect(() => {
  let cancelled = false;

  (async () => {
    if (!selectedCardId) return;

    try {
      setAbilityChangeInfo((prev) => ({
        ...prev,
        loading: true,
      }));

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cards/${encodeURIComponent(selectedCardId)}/ability-change-info`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      const data = await res.json().catch(() => ({}));
      if (cancelled) return;

      if (!res.ok) {
        throw new Error(data?.error || 'Failed to load ability change info');
      }

      setAbilityChangeInfo({
        loading: false,
        enabled: !!data.enabled,
        used: Number(data.used ?? 0),
        limit: Number(data.limit ?? 5),
        limitMode: data.limitMode === "overall" ? "overall" : "daily",
        nextPrice: data.nextPrice == null ? null : Number(data.nextPrice),
        basePrice: Number(data.basePrice ?? 0),
        payCurrency: data.payCurrency || null,
        resetAt: data.resetAt || null,
        refillPriceRunes: Number(data.refillPriceRunes ?? 0),
        canRefill: !!data.canRefill,
      });
    } catch (err) {
      if (!cancelled) {
        setAbilityChangeInfo({
          loading: false,
          enabled: false,
          used: 0,
          limit: 3,
          limitMode: "overall",
          nextPrice: null,
          basePrice: 0,
          payCurrency: null,
          resetAt: null,
          refillPriceRunes: 0,
          canRefill: false,
        });
      }
    }
  })();

  return () => {
    cancelled = true;
  };
}, [selectedCardId]);

const resetAbilityLimit = async () => {
  try {
    if (!selectedCard?.id) return;

    setAbilityRefillBusy(true);

    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/cards/${encodeURIComponent(selectedCard.id)}/reset-ability-change-limit`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(data?.error || "Failed to reset ability limit");
    }

    if (typeof setUser === "function") {
      setUser((u) => ({
        ...(u || {}),
        runes: Number(data?.runes ?? u?.runes ?? 0),
      }));
    }

    setAbilityChangeInfo((prev) => ({
      ...prev,
      used: 0,
      refillPriceRunes: Number(
        data?.refillPriceRunes ?? prev?.refillPriceRunes ?? 0
      ),
      canRefill: Number(
        data?.refillPriceRunes ?? prev?.refillPriceRunes ?? 0
      ) > 0,
    }));

    setAbilityRefillOpen(false);

    if (abilityTarget) {
      setAbilityChangeOpen(true);
    }
  } catch (err) {
    setErrorMessage(err?.message || "Failed to reset ability limit");
    setShowErrorModal(true);
  } finally {
    setAbilityRefillBusy(false);
  }
};

const openDefenseDeckRequiredModal = () => {
  setBattleSoonOpen(false);
  setPendingCard(null);
  setEntering(false);
  setDefenseDeckRequiredOpen(true);
};

const goToDefenseDeck = () => {
  setDefenseDeckRequiredOpen(false);
  onNavigate?.("defenseDeck");
};

const submitAbilityChange = async () => {
  if (!selectedCard || !abilityTarget) return;

  try {
    setAbilityChangeBusy(true);

    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/cards/${encodeURIComponent(selectedCard.id)}/change-ability`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ ability: abilityTarget }),
      }
    );

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
    if (
  data?.error === "ABILITY_CHANGE_LIMIT_REACHED" ||
  data?.error === "ABILITY_CHANGE_DAILY_LIMIT_REACHED"
) {
  const resolvedLimitMode =
    abilityChangeInfo.limitMode === "overall" ? "overall" : "daily";

  const refillPriceRunes = Number(
    data.refillPriceRunes ?? abilityChangeInfo.refillPriceRunes ?? 0
  );

  setAbilityChangeInfo({
    loading: false,
    enabled: false,
    used: Number(data.used ?? 0),
    limit: Number(data.limit ?? 5),
    limitMode: resolvedLimitMode,
    nextPrice: null,
    basePrice: Number(data.basePrice ?? 0),
    payCurrency: data.payCurrency || null,
    resetAt: data.resetAt || null,
    refillPriceRunes,
    canRefill: refillPriceRunes > 0,
  });

  setAbilityChangeOpen(false);

  if (refillPriceRunes > 0) {
    setAbilityRefillOpen(true);
    return;
  }

  setErrorMessage(
    resolvedLimitMode === "overall"
      ? "Overall limit reached for ability changes."
      : "Daily limit reached for ability changes."
  );
  setShowErrorModal(true);
  return;
}

      throw new Error(data?.error || 'Failed to change ability');
    }

    if (data.card) {
      setCards((prev) =>
        prev.map((c) => (c.id === data.card.id ? { ...c, ...data.card } : c))
      );
    }

    if (data.user && typeof setUser === 'function') {
      setUser((prev) => ({ ...(prev || {}), ...data.user }));
    }

    setAbilityChangeInfo((prev) => ({
      ...prev,
      loading: false,
      enabled: true,
      used: Number(data.used ?? prev.used ?? 0),
      limit: Number(data.limit ?? prev.limit ?? 5),
      limitMode: data.limitMode === "overall" ? "overall" : "daily",
      nextPrice: data.nextPrice == null ? null : Number(data.nextPrice),
      basePrice: Number(data.basePrice ?? prev.basePrice ?? 0),
      payCurrency: data.payCurrency || prev.payCurrency || null,
      resetAt: data.resetAt || null,
    }));

    setAbilityChangeOpen(false);
    setAbilityTarget(null);
  } catch (err) {
    setErrorMessage(err.message || 'Failed to change ability');
    setShowErrorModal(true);
  } finally {
    setAbilityChangeBusy(false);
  }
};

  const openRename = () => {
    if (!selectedCard) return;
    setRenameValue(selectedCard.name || '');
    setRenameOpen(true);
  };

  const submitRename = async (e) => {
    e?.preventDefault?.();
    if (!selectedCard) return;

    const trimmed = String(renameValue || '').trim();
    if (!trimmed) {
      setErrorMessage('Please enter a name.');
      setShowErrorModal(true);
      return;
    }
    if (trimmed.length > 32) {
      setErrorMessage('Name is too long (max 32 chars).');
      setShowErrorModal(true);
      return;
    }

    const have = Number(user?.sparks ?? 0);
    const need = Number(nameChangePrice ?? 0);
    if (need > 0 && have < need) {
      setErrorMessage(
        `You need ${need} Sparks to rename, but you only have ${have}.`
      );
      setShowErrorModal(true);
      return;
    }

    try {
      setRenameBusy(true);
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cards/${encodeURIComponent(
          selectedCard.id
        )}/rename`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({ name: trimmed }),
        }
      );
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || 'Failed to rename card');
      }

      if (data.card) {
        setCards((prev) =>
          prev.map((c) => (c.id === data.card.id ? { ...c, ...data.card } : c))
        );
      }

      if (data.user?.sparks !== undefined && typeof setUser === 'function') {
        setUser((prev) => ({ ...(prev || {}), sparks: data.user.sparks }));
      }

      setRenameOpen(false);
    } catch (err) {
      setErrorMessage(err.message || 'Failed to rename card');
      setShowErrorModal(true);
    } finally {
      setRenameBusy(false);
    }
  };



  useEffect(() => {
    if (!battleSoonOpen) return;
    const randomTag = battleTaglines[Math.floor(Math.random() * battleTaglines.length)];
    setTagline(randomTag);
    const shuffled = [...battleBullets].sort(() => 0.5 - Math.random());
    setBulletLines(shuffled.slice(0, 1));
  }, [battleSoonOpen]); // eslint-disable-line

  /* ---------- Reward % (exactly like Fight.js) ---------- */
  const rewardPercent = useMemo(() => {
    const raw =
      shop?.rewardPercent ??
      shop?.REWARD_PERCENT ??
      shop?.fight?.rewardPercent;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }, [shop]);

  // projected win numbers (same as Fight.js)
  const { winBonus, projectedWin } = useMemo(() => {
    const fee = Number(entryFee || 0);
    const bonus = Math.floor(fee * (rewardPercent / 100));
    return { winBonus: Math.max(0, bonus), projectedWin: fee + Math.max(0, bonus) };
  }, [entryFee, rewardPercent]);

  /* ---------- Config-derived costs ---------- */
  const RUNE_COST_BY_RARITY = useMemo(() => ({
    common: Number(shop?.evolve?.common) || 5,
    rare: Number(shop?.evolve?.rare) || 10,
    epic: Number(shop?.evolve?.epic) || 15,
    legendary: Number(shop?.evolve?.legendary) || 20
  }), [shop]);

  const fightRewards = useMemo(() => ({
    common: Number(shop?.fight?.common) || 0,
    rare: Number(shop?.fight?.rare) || 0,
    epic: Number(shop?.fight?.epic) || 0,
    legendary: Number(shop?.fight?.legendary) || 0
  }), [shop]);

  const EVOLVE_MULTIPLIER = Number(process.env.REACT_APP_EVOLVE_MULTIPLIER) || 1.25;
  function uiEvolveCost(card, baseByRarityFn) {
    if (!card) return 0;
    const base = baseByRarityFn(card.rarity);
    const timesEvolved = Math.max(0, (card.level || 1) - 1);
    return Math.ceil(base * Math.pow(EVOLVE_MULTIPLIER, timesEvolved));
  }

  /* ---------- Helpers ---------- */
  const MAX_SALE_PRICE = Number(process.env.REACT_APP_MAX_SALE_PRICE) || 10000;
  function clampSalePrice(v) {
    const n = Math.floor(Number(v) || 0);
    if (n <= 0) return '';
    return String(Math.min(n, MAX_SALE_PRICE));
  }

  const normRarity = (r) => String(r || 'Common').toLowerCase();
  const pickProfile = (rarity) => {
    const key = normRarity(rarity);
    if (key === 'common')    return RARITY_PROFILES.Common;
    if (key === 'rare')      return RARITY_PROFILES.Rare;
    if (key === 'epic')      return RARITY_PROFILES.Epic;
    if (key === 'legendary') return RARITY_PROFILES.Legendary;
    return RARITY_PROFILES.Common;
  };
  const nextEvolveCost = (rarity) => RUNE_COST_BY_RARITY[normRarity(rarity)] ?? 5;
  const fightRewardFor = (rarity) => fightRewards[normRarity(rarity)] ?? 0;

  useEffect(() => {
  if (!ABILITY_CHANGE_HINT_ENABLED) {
    setShowAbilityHint(false);
    return;
  }

  if (!selectedCard) {
    setShowAbilityHint(false);
    return;
  }

  const alreadySeen =
    localStorage.getItem(ABILITY_HINT_STORAGE_KEY) === "true";

  if (alreadySeen) {
    setShowAbilityHint(false);
    return;
  }

  const abilities = sanitizeAbilityList(parseAbilities(selectedCard?.abilities));
  if (!abilities.length) {
    setShowAbilityHint(false);
    return;
  }

  setHintAbilityIndex(0);
  setShowAbilityHint(true);

  const timeout = setTimeout(() => {
    setShowAbilityHint(false);
  }, 5000);

  return () => clearTimeout(timeout);
}, [selectedCard]);

function getEvolveOptions(card) {
  const rarity = String(card?.rarity || '').toLowerCase();
  const level = Number(card?.level || 1);

  const list =
    evolvements?.prices?.[rarity] ??
    evolvements?.[rarity] ??
    [];

  if (Array.isArray(list)) {
    const row = list.find((p) => Number(p?.level) === level);

    const runes = Number(row?.runes || row?.runeCost || 0);
    const cardPieces = Number(row?.cardPieces || row?.pieces || 0);
    const sparks = Number(row?.sparks || 0);

    if (runes > 0 || cardPieces > 0 || sparks > 0) {
      const defaultPayWith =
        sparks > 0 ? 'sparks' :
        runes > 0 ? 'runes' :
        cardPieces > 0 ? 'pieces' :
        null;

      return {
        isLegacy: false,
        defaultPayWith,
        options: {
          runes,
          cardPieces,
          sparks,
        },
      };
    }
  }

  return {
    isLegacy: true,
    defaultPayWith: 'sparks',
    options: {
      sparks: uiEvolveCost(card, nextEvolveCost),
    },
  };
}

const isCardPiecesItem = (inv) => {
  return normalizeItemType(inv?.item?.type) === "cardPieces";
};

const getCardPieceItemAmount = (inv) => {
  const raw =
    inv?.amount ??
    inv?.item?.card_pieces_amount ??
    inv?.item?.cardPiecesAmount ??
    inv?.item?.pieces ??
    inv?.quantity ??
    0;

  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const claimableCardPieceItems = useMemo(() => {
  return invItems
    .filter((inv) => isCardPiecesItem(inv))
    .filter((inv) => !inv.claimed)
    .filter((inv) => getCardPieceItemAmount(inv) > 0);
}, [invItems]);

const ensureInventoryLoaded = async () => {
  if (inventoryLoaded || inventoryLoading) return;

  try {
    setInventoryLoading(true);
    setPieceClaimError("");

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("Not logged in.");
    }

    const res = await fetch(`${API_BASE}/api/inventory`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(data?.error || "Failed to load inventory.");
    }

    setInvItems(Array.isArray(data.items) ? data.items : []);
    setInventoryLoaded(true);
  } catch (err) {
    setPieceClaimError(err?.message || "Failed to load card pieces.");
  } finally {
    setInventoryLoading(false);
  }
};

const claimCardPiecesIntoCard = async (inv, card) => {
  if (!inv?.user_item_id || !card?.id) return;
  if (pieceClaimBusyId) return;

  try {
    setPieceClaimBusyId(inv.user_item_id);
    setPieceClaimError("");

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("Not logged in.");
    }

    const res = await fetch(
      `${API_BASE}/api/inventory/${encodeURIComponent(inv.user_item_id)}/claim`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ card_id: card.id }),
      }
    );

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(data?.error || "Failed to claim card pieces.");
    }

    setInvItems((prev) =>
      prev.map((row) =>
        row.user_item_id === inv.user_item_id
          ? {
              ...row,
              claimed: true,
              claimed_at:
                data.claimed_at ||
                row.claimed_at ||
                new Date().toISOString(),
              applied_card_id:
                data.applied_card_id ?? data.card?.id ?? card.id,
            }
          : row
      )
    );

    if (data.card) {
      setCards((prev) =>
        prev.map((c) =>
          Number(c.id) === Number(data.card.id)
            ? { ...c, ...data.card }
            : c
        )
      );

      setEvolveConfirmCard((prev) =>
        prev && Number(prev.id) === Number(data.card.id)
          ? { ...prev, ...data.card }
          : prev
      );
    }
  } catch (err) {
    setPieceClaimError(err?.message || "Failed to claim card pieces.");
  } finally {
    setPieceClaimBusyId(null);
  }
};

  const parseAbilities = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try { return JSON.parse(raw); } catch { return []; }
  };
  const sanitizeAbilityList = (list) =>
    (list || [])
      .map(a => (typeof a === 'string' ? a : (a?.name || '')))
      .map(s => String(s).trim())
      .filter(s => s && ABILITIES.includes(s));

  const underAbilityCap = (abilities, rarity) => {
    const prof = pickProfile(rarity);
    const [, maxAbilities] = prof.abilities;
    return (abilities?.length || 0) < maxAbilities;
  };
  const canCardImprove = (card) => {
    if (!card) return false;
    const p = pickProfile(card.rarity);
    const canStat =
      (card.attack  ?? 0) < p.atk[1] ||
      (card.defense ?? 0) < p.def[1] ||
      (card.health  ?? 0) < p.hp[1];
    const abilitiesNow = sanitizeAbilityList(parseAbilities(card.abilities));
    const canAb = underAbilityCap(abilitiesNow, card.rarity);
    return canStat || canAb;
  };

  useEffect(() => {
  let cancelled = false;
  (async () => {
    if (!battleSoonOpen || !pendingCard?.id) return;

    // show spinner state on each open
    setLimitInfo({ loading: true, used: 0, limit: 10, resetAt: null });

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/battle/limit?cardId=${encodeURIComponent(pendingCard.id)}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      const data = await res.json().catch(() => ({}));
      if (cancelled) return;

      if (res.ok && Number.isFinite(Number(data?.limit))) {
        setLimitInfo({
          loading: false,
          used: Number(data.used ?? 0),
          limit: Number(data.limit ?? 10),
          resetAt: data.resetAt || null,
        });
      } else {
        setLimitInfo((i) => ({ ...i, loading: false }));
      }
    } catch {
      if (!cancelled) setLimitInfo((i) => ({ ...i, loading: false }));
    }
  })();
  return () => { cancelled = true; };
}, [battleSoonOpen, pendingCard?.id]);


  useEffect(() => {
  // Only show countdown when:
  //  - we’re not loading
  //  - we have a resetAt timestamp
  //  - and the daily limit has actually been hit
  if (
    limitInfo.loading ||
    !limitInfo.resetAt ||
    limitInfo.used < limitInfo.limit
  ) {
    setCooldownLabel(null);
    return;
  }

  const resetTs = new Date(limitInfo.resetAt).getTime();
  if (!Number.isFinite(resetTs)) {
    setCooldownLabel(null);
    return;
  }

  const update = () => {
    const diff = resetTs - Date.now();

    if (diff <= 0) {
      // Local auto-unlock once the reset time passes
      setLimitInfo((prev) => ({
        ...prev,
        used: 0,
        resetAt: null,
        loading: false,
      }));
      setCooldownLabel(null);
      return;
    }

    const totalSeconds = Math.floor(diff / 1000);
    const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
    const minutes = String(
      Math.floor((totalSeconds % 3600) / 60)
    ).padStart(2, "0");
    const seconds = String(totalSeconds % 60).padStart(2, "0");

    setCooldownLabel(`${hours}:${minutes}:${seconds}`);
  };

  update(); // run once immediately
  const id = window.setInterval(update, 1000);
  return () => window.clearInterval(id);
}, [limitInfo.loading, limitInfo.resetAt, limitInfo.used, limitInfo.limit]);



  /* ---------- Load selected card ---------- */
  useEffect(() => {
    const fetchCard = async () => {
      if (!selectedCardId) return;
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/shop/cards/${selectedCardId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const data = await res.json();
        setCards([data.card]);
      } catch (err) {
        console.error('Failed to load card:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCard();
  }, [selectedCardId]);

  /* ---------- Compute entry fee when opening battle (same as Fight.js) ---------- */
  useEffect(() => {
    if (!pendingCard) return;
    const toNum = (v) => {
      const n = Number(v);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    };
    const rarity = String(pendingCard.rarity || 'Common').toLowerCase();
    const feeByRarity = {
      common:    toNum(shop?.fightCost?.common ?? shop?.fight?.common),
      rare:      toNum(shop?.fightCost?.rare   ?? shop?.fight?.rare),
      epic:      toNum(shop?.fightCost?.epic   ?? shop?.fight?.epic),
      legendary: toNum(shop?.fightCost?.legendary ?? shop?.fight?.legendary),
    };
    setEntryFee(feeByRarity[rarity] ?? 0);
    setFeeLoading(false);
  }, [pendingCard, shop]);

  /* ---------- Marketplace fee preview (unchanged) ---------- */
  const [feePct, setFeePct] = useState(null);
  function roundedFeeFor(price, pct) {
    const p = Number(price) || 0;
    const percent = Number(pct) || 0;
    const raw = (p * percent) / 100;
    const f = Math.ceil(raw);
    return Math.max(0, Math.min(p, f));
  }
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/cards/marketplace-fee`);
        const data = await res.json();
        if (!cancelled) setFeePct(Number(data?.feePercent ?? 0));
      } catch {
        if (!cancelled) setFeePct(Number(process.env.REACT_APP_MARKETPLACE_FEE) || 0);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const priceNum = Number(sellPrice) || 0;
  const effectiveFeePct = feePct == null ? 0 : feePct;
  const feePreview = roundedFeeFor(priceNum, effectiveFeePct);
  const netPreview = Math.max(0, priceNum - feePreview);

  async function unlistCard(cardId) {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/cards/${cardId}/sell`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || 'Failed to unlist');
    return true;
  }

  const openPieceConvertConfirm = (card) => {
  if (!card) return;

  const level = Number(card.level || 1);
  const pieces = Number(card.pieces || 0);

  if (level !== MAX_LEVEL) {
    setErrorMessage("Only level 10 cards can convert pieces into sparks.");
    setShowErrorModal(true);
    return;
  }

  if (pieces <= 0) {
    setErrorMessage("This card has no pieces to convert.");
    setShowErrorModal(true);
    return;
  }

  setPieceConvertCard(card);
  setPieceConvertOpen(true);
};

const closePieceConvertConfirm = () => {
  if (pieceConvertBusy) return;
  setPieceConvertOpen(false);
  setPieceConvertCard(null);
};

const confirmPieceConvert = async () => {
  if (!pieceConvertCard?.id || pieceConvertBusy) return;

  try {
    setPieceConvertBusy(true);

    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/cards/${encodeURIComponent(
        pieceConvertCard.id
      )}/convert-pieces-to-sparks`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(
        data?.message ||
        data?.error ||
        "Failed to convert card pieces."
      );
    }

    if (data.card) {
      setCards((prev) =>
        prev.map((card) =>
          Number(card.id) === Number(pieceConvertCard.id)
            ? { ...card, ...data.card }
            : card
        )
      );
    }

    if (data.user && typeof setUser === "function") {
      setUser((prev) => ({
        ...(prev || {}),
        ...data.user,
      }));
    }

    setPieceConvertOpen(false);
setPieceConvertCard(null);

setPieceConvertSuccess({
  piecesConverted: Number(data.piecesConverted || 0),
  sparksAwarded: Number(data.sparksAwarded || 0),
});
  } catch (err) {
    setErrorMessage(err?.message || "Failed to convert card pieces.");
    setShowErrorModal(true);
  } finally {
    setPieceConvertBusy(false);
  }
};

 const openEvolveConfirm = async (card) => {
  if (!card) return;
  if (evolvingId) return;

  if ((card?.level ?? 1) >= MAX_LEVEL) {
    setErrorMessage("This card is already at level 10.");
    setShowErrorModal(true);
    return;
  }

  setPieceClaimError("");
  setEvolveConfirmCard(card);
  setEvolveConfirmOpen(true);

  const p = getEvolveOptions(card);
  const cardPieces = Number(p.options?.cardPieces || 0);

  if (cardPieces > 0) {
    await ensureInventoryLoaded();
  }
};

const confirmEvolve = async (payWith) => {
  if (!evolveConfirmCard) return;

  const card = evolveConfirmCard;

  // close first (prevents double clicks / weirdness)
  setEvolveConfirmOpen(false);
  setEvolveConfirmCard(null);

  await handleEvolve(card, payWith);
};


 const handleEvolve = async (card, payWith) => {
  if (evolvingId) return;
  if ((card?.level ?? 1) >= MAX_LEVEL) {
    setErrorMessage('This card is already at level 10.');
    setShowErrorModal(true);
    return;
  }

  setEvolvingId(card.id);

  const oldStatsFallback = {
    attack: card.attack,
    defense: card.defense,
    health: card.health
  };
  const abilitiesBefore = sanitizeAbilityList(parseAbilities(card.abilities));

  try {
    const useNewRoute = payWith === "runes" || payWith === "pieces" || payWith === "sparks";

    const url = useNewRoute
      ? `${process.env.REACT_APP_API_URL}/api/cards/${card.id}/evolve-new`
      : `${process.env.REACT_APP_API_URL}/api/cards/${card.id}/evolve`;

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(payWith ? { payWith } : {})
    });

    const data = await res.json();

    if (!res.ok) {
      if (data?.error === 'EVOLVE_NO_PROGRESS') {
        setErrorMessage('Already at max allowed for this card type.');
        setShowErrorModal(true);
      } else {
        throw new Error(data?.error || 'Failed to evolve card');
      }
      return;
    }

    const updatedCard = data.card;
    setCards(prev => prev.map(c => (c.id === card.id ? { ...c, ...updatedCard } : c)));

    if (data.user?.sparks !== undefined || data.user?.runes !== undefined) {
      setUser(prev => ({ ...(prev || {}), ...(data.user || {}) }));
    }

    const oldStats = data.oldStats || oldStatsFallback;
    const newStats = data.newStats || {
      attack: updatedCard.attack,
      defense: updatedCard.defense,
      health: updatedCard.health,
    };

    const abilitiesAfter = sanitizeAbilityList(parseAbilities(updatedCard.abilities));
    const gained = abilitiesAfter.find(a => !abilitiesBefore.includes(a)) || null;

    setEvolveData({
      name: updatedCard.name || card.name,
      rarity: updatedCard.rarity || card.rarity,
      oldStats,
      newStats,
      abilityGained: gained,
      art: updatedCard?.character?.body
        ? `/assets/body/${updatedCard.character.body.side}/${updatedCard.character.body.rarity}/${updatedCard.character.body.variant}_thumb.png`
        : undefined,
    });

    setEvolveOpen(true);
  } catch (err) {
    setErrorMessage(err.message || 'Failed to evolve card');
    setShowErrorModal(true);
  } finally {
    setEvolvingId(null);
  }
};

  const openSell = (card) => {
    setSellingCard(card);
    setSellPrice(card?.sale_price ? String(card.sale_price) : '');
    setSellOpen(true);
  };

  const submitSell = async (e) => {
    e?.preventDefault?.();
    const priceNum = Number(sellPrice);
    if (!sellingCard) return;
    if (!Number.isFinite(priceNum) || priceNum <= 0) {
      setErrorMessage('Please enter a valid price.');
      setShowErrorModal(true);
      return;
    }

    try {
      setSellingBusy(true);
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/cards/${sellingCard.id}/sell`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ price: priceNum }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data?.error === 'PRICE_TOO_LOW' && Number.isFinite(Number(data?.minPrice))) {
          throw new Error(
            `Price too low. The minimum for ${sellingCard?.rarity || 'this'} is ${data.minPrice} runes.`
          );
        }

        throw new Error(data?.message || data?.error || 'Failed to list card for sale');
      }

      setCards(prev =>
        prev.map(c =>
          c.id === sellingCard.id ? { ...c, for_sale: true, sale_price: data.price ?? priceNum } : c
        )
      );

      setSellOpen(false);
      setSellingCard(null);
      setSellPrice('');
    } catch (err) {
      setErrorMessage(err.message || 'Failed to list card for sale');
      setShowErrorModal(true);
    } finally {
      setSellingBusy(false);
    }
  };

  const submitUnlist = async () => {
    if (!sellingCard?.for_sale) { setSellOpen(false); setSellingCard(null); return; }
    try {
      setSellingBusy(true);
      await unlistCard(sellingCard.id);
      setCards(prev => prev.map(c => c.id === sellingCard.id ? { ...c, for_sale: false, sale_price: null } : c));
      setSellOpen(false);
      setSellingCard(null);
      setSellPrice('');
    } catch (err) {
      setErrorMessage(err.message || 'Failed to unlist card');
      setShowErrorModal(true);
    } finally {
      setSellingBusy(false);
    }
  };


  if (loading) {
  return (
    <div key="character-loading" className="modal_box">
      <div
        className="modal_body"
        style={{
          minHeight: 430,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <LoadingSpinner text="Loading Card" size={64} />
      </div>
    </div>
  );
}

  const evolveDisabled = evolvingId === selectedCard?.id || ((selectedCard?.level ?? 1) >= MAX_LEVEL);
  const evolveCost = selectedCard ? uiEvolveCost(selectedCard, nextEvolveCost) : 0;
  const fightReward = selectedCard ? fightRewardFor(selectedCard.rarity) : 0;

const limitReached = !limitInfo.loading && (limitInfo.used >= limitInfo.limit);
const nextBattle = !limitInfo.loading ? Math.min(limitInfo.used + 1, limitInfo.limit) : null;


  return (
   <motion.div
  key={selectedCardId}
  className="modal_box"
>


      
{/* CARD PROFILE */}
{selectedCard && (() => {
  const card = selectedCard;
  const abilitiesSafe = sanitizeAbilityList(parseAbilities(card.abilities));
  const price = getEvolveOptions(card);
  const reward = fightRewardFor(card.rarity);

  const evolveBlocked =
    evolvingId === card.id || Number(card.level || 1) >= MAX_LEVEL;

const battleUsed = Number(limitInfo.used || 0);
const battleLimit = Number(limitInfo.limit || 10);

const battleProgress = battleLimit > 0
  ? Math.max(0, Math.min(100, (battleUsed / battleLimit) * 100))
  : 0;

  const currentPieces = Number(card.pieces || 0);
  const neededPieces = Number(price.options?.cardPieces || 0);

  const selectCardAndGoArena = () => {
  if (limitReached) return;

  localStorage.setItem("selectedCardId", String(card.id));

  localStorage.setItem(
    "selectedCardSnapshot",
    JSON.stringify({
      id: card.id,
      name: card.name,
      rarity: card.rarity,
      attack: card.attack,
      defense: card.defense,
      health: card.health,
      level: card.level,
      wins: card.wins,
      pieces: card.pieces,
      character: card.character,
      side: card.side,
      seasonal_accessory: card.seasonal_accessory ?? null,
      season_name: card.season_name ?? null,
      abilities: card.abilities ?? [],
    })
  );

  setSelectedCardId?.(card.id);

  closeCharacterModal?.();

  onNavigate?.("arenas", {
    cardId: card.id,
    openedVia: "inventoryHub",
  });
};

  return (
  <>
    <div className="modal_title">
      Card Profile

<button
  type="button"
  onClick={closeCharacterModal}
  aria-label="Close"
  className="modal_close"
/>
    </div>

    <div className="modal_body">
      <div className="modal_inside" style={{padding: 10}}>
      <div className="char-top-title shadow2">
        
        {truncateCardName(card.name)}

        {nameChangePrice > 0 && (
          <button
            type="button"
            className="char-edit-btn"
            onClick={openRename}
            title="Rename card"
          >
            Edit
          </button>
        )}
      </div>

      <div className="char-main-panel">

        <div className="char-left-card">
          <div style={{position: "absolute",
  top: 10,
  left: 8,
  width: 28,
  height: 28,
  backgroundImage: "url('/v2/position.png')",
  backgroundSize: "100% 100%",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
  color: "#fff",
  fontSize: 15,
  lineHeight: "28px",
  textAlign: "center",
  zIndex: 20,
  fontFamily: '"Fredoka", sans-serif',
  fontWeight: 600,}}
          >
            {card.level}
          </div>

          {card.for_sale && (
          <div className="char-sale-badge">
            FOR SALE
          </div>
        )}

          <div className="char-art-box">
            {Object.entries(card.character || {})
              .sort(([a], [b]) => {
                const order = ["background", "body", "head"];
                const indexA = order.includes(a) ? order.indexOf(a) : order.length;
                const indexB = order.includes(b) ? order.indexOf(b) : order.length;
                return indexA - indexB;
              })
              .map(([part, data]) => {
                const defaultSrc =
                  `/assets/${part}/${data.side}/${data.rarity}/${data.variant}_thumb.png`;

                const seasonName =
                  card?.seasonal_accessory?.season_name ||
                  card?.season_name ||
                  null;

                const src = getSkinSrc(seasonName, part, defaultSrc);

                const zIndexMap = {
                  background: 1,
                  body: 2,
                  head: 11,
                };

                return (
                  <img
                    key={part}
                    src={src}
                    alt={part}
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      zIndex: zIndexMap[part] ?? 10,
                      pointerEvents: "none",
                    }}
                  />
                );
              })}
          </div>

          <div style={{zIndex: 20,
    textTransform: "uppercase",
    position: "absolute",
    bottom: -3,
    left: 3,
    right: 3,
    color: "#fff",
    height: 21,
    fontSize: 11,
    lineHeight: "21px",
    textAlign: "center",
    fontWeight: 600,
    background: "rgba(77, 44, 36, 0.65)"}}>
            {card.rarity}
          </div>
        </div>

        <div className="char-right-info">
          <div className="char-stats-title">
            Card Stats
          </div>

          <div className="char-stat-line">
            <img src="/s_attack.png" alt="" />
            <span>Attack</span>
            <b>{card.attack}</b>
          </div>

          <div className="char-stat-line">
            <img src="/s_defense.png" alt="" />
            <span>Defense</span>
            <b>{card.defense}</b>
          </div>

          <div className="char-stat-line">
            <img src="/s_health.png" alt="" />
            <span>Health</span>
            <b>{card.health}</b>
          </div>

          <button
            type="button"
            className="char-evolve-button"
            disabled={evolveBlocked}
            onClick={() => !evolveBlocked && openEvolveConfirm(card)}
            title={card.level >= MAX_LEVEL ? "Max level reached" : undefined}
          >
            <span>
              {card.level >= MAX_LEVEL ? "Max Level" : "Evolve it"}
            </span>

            {card.level < MAX_LEVEL && (
              <small>
                Use:
                {Number(price.options?.sparks || 0) > 0 && (
                  <img src="/sparks.png" alt="Sparks" />
                )}
                {Number(price.options?.runes || 0) > 0 && (
                  <img src="/runes.png" alt="Runes" />
                )}
                {Number(price.options?.cardPieces || 0) > 0 && (
                  <img src="/pieces.png" alt="Pieces" />
                )}
              </small>
            )}
          </button>
        </div>

        <div className="char-level-progress">
  <div className="char-level-fill" style={{ width: `${battleProgress}%` }} />
  <span>
    {limitInfo.loading ? "..." : `${battleUsed}/${battleLimit}`}
  </span>
</div>
      </div>

      <div className="char-abilities-list">
        {abilitiesSafe.map((ability, idx) => {
          const abilityKey = ability.toLowerCase();
          const info = abilities_desc.find(
            a => a.id.toLowerCase() === abilityKey
          );

          return (
            <div className="char-ability-row" key={`${ability}-${idx}`}>
              <div className="char-ability-icon">
                <img src={`/a_${abilityKey}.png`} alt={ability} />
              </div>

              <div className="char-ability-text">
                <strong>{ability}</strong>
                <span>{info?.desc || "No description available."}</span>
              </div>

              <button
                type="button"
                className="char-edit-btn"
                disabled={!abilityChangeEnabled}
                onClick={() => openAbilityChange(ability)}
              >
                Change
              </button>
            </div>
          );
        })}
      </div>

      <div className="char-bottom-stats">
        <div>
          <span>Total Wins</span>
          <b>{card.wins ?? 0}</b>
        </div>

        <div
  className={Number(card.level || 1) === MAX_LEVEL && Number(card.pieces || 0) > 0 ? "clickable" : ""}
  role={Number(card.level || 1) === MAX_LEVEL && Number(card.pieces || 0) > 0 ? "button" : undefined}
  tabIndex={Number(card.level || 1) === MAX_LEVEL && Number(card.pieces || 0) > 0 ? 0 : undefined}
  onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();

    if (Number(card.level || 1) === MAX_LEVEL && Number(card.pieces || 0) > 0) {
      openPieceConvertConfirm(card);
    }
  }}
  title={
    Number(card.level || 1) === MAX_LEVEL && Number(card.pieces || 0) > 0
      ? "Convert card pieces into Sparks"
      : "Card pieces"
  }
>
  <span>Card Pieces</span>
  <b>{card.pieces ?? 0}</b>
</div>
      </div>
      </div>


      <div className="modal_footer">
        <button
          type="button"
          className="game-button green-button" style={{marginRight: 10}}
          onClick={() => openSell(card)}
        >
          Sell
        </button>

        
          <button
  type="button"
  className="game-button yellow-button"
  disabled={limitReached}
  onClick={selectCardAndGoArena}
   style={{marginRight: 10}}
>
  {limitReached ? cooldownLabel || "Limit" : "FIGHT"}
</button>

 <button
  type="button"
  onClick={closeCharacterModal}
  className="game-button red-button"
>
  CLOSE
</button>
        
      </div>
</div>
  </>
);
})()}

      
      {pieceConvertOpen && pieceConvertCard && (() => {
  const pieces = Number(pieceConvertCard?.pieces || 0);
  const rate = Number(pieceConvertCard?.pieceConversionRate || 0);
  const expectedSparks = rate > 0 ? Math.floor(pieces * rate) : null;

  return (
    <motion.div className="modal_box">

      <div className="modal_title">Card Pieces To Sparks
<button
            type="button"
            onClick={closePieceConvertConfirm}
            disabled={pieceConvertBusy}
            className="modal_close"
          />
      </div>

      <div className="modal_body">
        <div className="modal_inside">
        <img
          src="/pieces.png"
          alt="Pieces"
          style={{ height: "70px", marginBottom: "10px" }}
        />

        <div className="shadow2" style={{ fontSize: 28, fontWeight: 600, marginBottom: "10px" }}>
          Convert Pieces?
        </div>

        <div
        className="shadow1"
          style={{
            marginBottom: 10,
            color: "#fff",
            fontSize: 21,
            fontWeight: 600
          }}
        >
          Convert{" "}
          <span style={{ color: "#ffdf5f" }}>
            {pieces}
          </span>{" "}
          card pieces from{" "}
          <span style={{ color: "#09BDFF" }}>
            {pieceConvertCard.name}
          </span>{" "}
          into Sparks?
        </div>

        <div className="info">
        {expectedSparks !== null && (
          <div
            style={{
              fontSize: 21,fontWeight: 600
            }}
          >
          Credit:&nbsp;
            <img
              src="/sparks.png"
              alt="Sparks"
              style={{ width: 24, height: 24, verticalAlign: "middle" }}
            />
            &nbsp;+{expectedSparks}
          </div>
        )}
        </div>

        {expectedSparks === null && (
          <div
            style={{
              color: "rgba(255,255,255,0.75)",
              fontSize: 16,
              marginBottom: 14,
              textShadow: "1px 1px #000",
            }}
          >
            The backend conversion rate will be applied.
          </div>
        )}

        </div>

        <div className="modal_footer">
          <button
            type="button"
            className="game-button green-button"
            onClick={confirmPieceConvert}
            disabled={pieceConvertBusy}
            style={{ marginRight: 10}}
          >
            {pieceConvertBusy ? "Converting..." : "Confirm"}
          </button>

          <button
            type="button"
            onClick={closePieceConvertConfirm}
            disabled={pieceConvertBusy}
            className="game-button red-button"
          >
            CANCEL
          </button>
        </div>
    </div>
      </motion.div>
  );
})()}

      {/* NEW: CONFIRM EVOLVE MODAL */}
{evolveConfirmOpen && evolveConfirmCard && (
  <motion.div className="modal_box"  initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>

      <div className="modal_title">Card Evolvement

      <button className="modal_close" onClick={() => {
    if (pieceClaimBusyId) return;
    setPieceClaimError("");
    setEvolveConfirmOpen(false);
    setEvolveConfirmCard(null);

  }}/>
      </div>

      
    <div className="modal_body">

      <div className="modal_inside">

      <img
        src="/finger.png"
        alt="Confirm"
        style={{ height: '80px', marginBottom: '10px' }}
      />

      <div className="shadow2" style={{ fontWeight: 600, fontSize: 28, marginBottom: '10px' }}>
        Confirm Evolve?
      </div>

      <div
      className="shadow1"
  style={{
    fontSize: 21,
    fontWeight: 600
  }}
>
  <div
    style={{
      width: "100%",
      maxWidth: "100%",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }}
    title={evolveConfirmCard.name}
  >
    Evolving Card{" "}
    <span style={{ color: "#09BDFF" }}>
      {evolveConfirmCard.name}
    </span>
  </div>

  {(() => {
    const p = getEvolveOptions(evolveConfirmCard);

    if (p.isLegacy) {
      return (
        <div style={{ marginTop: 4 }}>
          for {Number(p.options?.sparks || 0)} Sparks?
        </div>
      );
    }

    return (
      <div
      className="shadow2"
        style={{
          marginTop: 15,
          fontSize: 21,
        }}
      >
        Payment Method:
      </div>
    );
  })()}
</div>

     {(() => {
  const p = getEvolveOptions(evolveConfirmCard);
  const runes = Number(p.options?.runes || 0);
  const cardPieces = Number(p.options?.cardPieces || 0);
  const sparks = Number(p.options?.sparks || 0);

  return (
  <div
        className="info"
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
      marginTop: 20,
      marginLeft: -10,
      marginRight: -10,
      alignItems: 'center',
      width: 'calc(100% + 20px)',
    }}
  >
   
    {cardPieces > 0 && (
      <div
        style={{
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            marginBottom: 8,
          }}
        >
          <div
          className="shadow1"
            style={{
              fontWeight: 600,
              fontSize: 16,
              whiteSpace: "nowrap",
            }}
          >
            Inventory Pieces
          </div>

          <div
            style={{
              color: "#fff",
              fontSize: 14,
              lineHeight: "18px",
              textShadow: "1px 1px #000",
              display: "flex",
              alignItems: "center",
              gap: 3,
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ color: "#ffdf5f", fontSize: 15 }}>
              To Evolve
            </span>
            <img
              src="/pieces.png"
              alt=""
              style={{width: 18, height: 18, objectFit: "contain" }}
            />
            {Number(evolveConfirmCard?.pieces || 0)}/{cardPieces}
          </div>
        </div>

        {inventoryLoading ? (
          <div
            style={{
              color: "rgba(255,255,255,0.72)",
              fontSize: 13,
              textAlign: "center",
              padding: "2px 0",
            }}
          >
            Loading card pieces...
          </div>
        ) : claimableCardPieceItems.length > 0 ? (
          <div
  style={{
    display: "flex",
    alignItems: "stretch",
    justifyContent:
      claimableCardPieceItems.length <= 3 ? "center" : "flex-start",
    gap: 6,
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    paddingBottom: 2,
    width: "100%",
  }}
>
            {claimableCardPieceItems.map((inv) => {
              const amount = getCardPieceItemAmount(inv);
              const busy =
                Number(pieceClaimBusyId) === Number(inv.user_item_id);

              return (
                <div
                  key={inv.user_item_id}
                  style={{
                    flex: "0 0 auto",
                    width: 78,
                    minHeight: 74,
                    border: "1px solid rgba(255,223,95,0.3)",
                    borderRadius: 6,
                    background: "rgba(35,20,10,0.86)",
                    color: "#fff",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    padding: "7px 6px",
                    textShadow: "1px 1px #000",
                    boxSizing: "border-box",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 2,
                      fontSize: 15,
                      fontWeight: 600,
                      color: "#ffdf5f",
                    }}
                  >
                    <img
                      src="/pieces.png"
                      alt=""
                      style={{ width: 18, height: 18, objectFit: "contain" }}
                    />
                    +{amount}
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      claimCardPiecesIntoCard(inv, evolveConfirmCard)
                    }
                    disabled={!!pieceClaimBusyId || !!evolvingId}
                    style={{
                      width: 56,
                      height: 24,
                      border: "1px solid rgba(255,255,255,0.28)",
                      borderRadius: 7,
                      background: "linear-gradient(#42d85f, #168f32)",
                      boxShadow: "0 2px 0 rgba(0,0,0,0.35)",
                      color: "#fff",
                      cursor:
                        pieceClaimBusyId || evolvingId
                          ? "not-allowed"
                          : "pointer",
                      opacity: pieceClaimBusyId || evolvingId ? 0.65 : 1,
                      textAlign: "center",
                      lineHeight: "24px",
                      textShadow: "1px 1px rgba(0,0,0,0.7)",
                      fontWeight: 600,
                      fontSize: 12,
                      padding: 0,
                    }}
                  >
                    {busy ? "..." : "CLAIM"}
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div
            style={{
              color: "rgba(255,255,255,0.72)",
              fontSize: 16,
              textAlign: "center",
              padding: "2px 0",
            }}
          >
            No unclaimed card pieces available.
          </div>
        )}

        {pieceClaimError && (
          <div
            style={{
              marginTop: 6,
              color: "#ff6b6b",
              fontSize: 13,
              textAlign: "center",
              textShadow: "1px 1px #000",
            }}
          >
            {pieceClaimError}
          </div>
        )}
      </div>
    )}

    {!p.isLegacy ? (
  <div
    style={{
      display: 'flex',
      gap: 3,
      width: '100%',
      justifyContent: 'center',
    }}
  >
    {sparks > 0 && (
      <button
        onClick={() => confirmEvolve('sparks')}
        disabled={!!evolvingId}
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
        }}
      >
        {evolvingId ? (
          'Evolving...'
        ) : (
          <>
            <span>Sparks</span>
            <span
              style={{
                paddingLeft: '18px',
                backgroundImage: "url('/sparks.png')",
                backgroundSize: '16px',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'left center',
              }}
            >
              {sparks}
            </span>
          </>
        )}
      </button>
    )}

    {runes > 0 && (
      <button
        className="game-button green-button"
        onClick={() => confirmEvolve('runes')}
        disabled={!!evolvingId}
        style={{
          marginTop: 10,
          marginRight: 2,
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
        }}
      >
        {evolvingId ? (
          'Evolving...'
        ) : (
          <>
            <span>Runes</span>
            <span
              style={{
                paddingLeft: '18px',
                backgroundImage: "url('/runes.png')",
                backgroundSize: '16px',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'left center',
              }}
            >
              {runes}
            </span>
          </>
        )}
      </button>
    )}
    
   {cardPieces > 0 && (() => {
  const currentPieces = Number(evolveConfirmCard?.pieces || 0);
  const hasEnoughPieces = currentPieces >= cardPieces;

  return (
    <button
    className="game-button green-button"
      onClick={() => confirmEvolve('pieces')}
      disabled={!!evolvingId || !hasEnoughPieces}
      title={
        hasEnoughPieces
          ? "Evolve using card pieces"
          : `Need ${cardPieces - currentPieces} more pieces`
      }
      style={{
        marginTop: 10,
        marginLeft: 2,
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        opacity: hasEnoughPieces ? 1 : 0.55,
        cursor: hasEnoughPieces ? "pointer" : "not-allowed",
      }}
    >
      {evolvingId ? (
        'Evolving...'
      ) : (
        <>
          <span>Pieces</span>
          <span
            style={{
              paddingLeft: '18px',
              backgroundImage: "url('/pieces.png')",
              backgroundSize: '16px',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'left center',
            }}
          >
            {cardPieces}
          </span>
        </>
      )}
    </button>
  );
})()}
  </div>
) : (
  <button onClick={() => confirmEvolve('sparks')} disabled={!!evolvingId}>
    {evolvingId ? 'Evolving...' : 'Confirm'}
  </button>
)}

    </div>
  );
})()}
    
    </div>

    <div className="modal_footer">
          <button
          className="game-button red-button"
  onClick={() => {
    if (pieceClaimBusyId) return;
    setPieceClaimError("");
    setEvolveConfirmOpen(false);
    setEvolveConfirmCard(null);

  }}
  disabled={!!evolvingId || !!pieceClaimBusyId}
  style={{ minWidth: 140 }}
>
  CANCEL
</button>

</div>
  </div>
  </motion.div>
)}


      {/* EVOLVE MODAL */}
      <AnimatePresence>
        {evolveOpen && evolveData && (
          <motion.div className="modal_box"  initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>

            <div className="modal_title">Card Evolvement<button onClick={() => { setEvolveOpen(false); setEvolveData(null); }} className="modal_close"/></div>
            
            <div className="modal_body">

            <div className="modal_inside">
              {[...Array(16)].map((_, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, scale: 0.3 }}
                  animate={{
                    opacity: [0, 1, 0],
                    scale: [0.3, 1.1, 0.8],
                    x: Math.cos((i / 16) * Math.PI * 2) * 140,
                    y: Math.sin((i / 16) * Math.PI * 2) * 140
                  }}
                  transition={{ duration: 1.2, delay: 0.05 * i, ease: 'easeOut' }}
                  style={{
                    position: 'absolute', top: '50%', left: '50%', width: 8, height: 8,
                    borderRadius: '50%', background: 'linear-gradient(#fff,#ffd700)', pointerEvents: 'none'
                  }}
                />
              ))}

              <div className="shadow2" style={{fontWeight: 600, fontSize: 21,marginBottom: 15}}>{evolveData.name} Evolved!</div>

              <div className="info" style={{paddingBottom: 0}}>
              <div className="shadow1" style={{fontSize: 21, color: '#9adcfb', marginBottom: 15 }}>{evolveData.rarity}</div>

              {evolveData.abilityGained && (
                <motion.div
                  initial={{ y: -6, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 220, damping: 16 }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 12,
                    background: 'rgba(255, 223, 95, 0.12)', border: '1px solid rgba(255,223,95,0.35)',
                    color: '#ffdf5f', marginBottom: 10
                  }}
                >
                  <span style={{ display: 'inline-flex', width: 28, height: 28, alignItems: 'center', justifyContent: 'center',
                    backgroundImage: "url('/abl.png')", backgroundSize: '28px', backgroundRepeat: 'no-repeat', backgroundPosition: 'center center' }}>
                    <img src={`/a_${String(evolveData.abilityGained).toLowerCase()}.png`} alt={evolveData.abilityGained} style={{ width: 22, height: 22 }} />
                  </span>
                  <strong style={{ fontSize: 18 }}>New Ability:</strong>
                  <span style={{ color: '#fff', fontSize: 18 }}>{evolveData.abilityGained}</span>
                </motion.div>
              )}

              <div style={{ float: 'left', width: '100%', display: 'block', fontSize: 18, margin: '12px 0' }}>
  {['attack','defense','health'].map((key) => {
    const oldVal = evolveData.oldStats[key];
    const newVal = evolveData.newStats[key];
    const delta = newVal - oldVal;
    return (
      <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, textTransform: 'capitalize'}}>
          <img src={`/s_${key}.png`} alt="" style={{ width: 18, height: 18, objectFit: 'contain' }} />
          {key}:
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end'}}>
          <div style={{ textAlign: 'center', width: 25 }}>{oldVal}</div>
          <div style={{ textAlign: 'center', width: 20 }}>→</div>
          <div style={{ textAlign: 'right', width: 70 }}>
            {newVal} ({delta >= 0 ? `+${delta}` : delta})
          </div>
        </div>
      </div>
    );
  })}
</div></div></div>

              <div className="modal_footer">
                <button className="game-button red-button" onClick={() => { setEvolveOpen(false); setEvolveData(null); }}>CLOSE</button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* SELL MODAL */}
      <AnimatePresence>
        {sellOpen && sellingCard && (
          <motion.div className="modal_box" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>

            <div className="modal_title">Marketplace Listing
            <button className="modal_close" onClick={() => { setSellOpen(false); setSellingCard(null); setSellPrice(''); }} disabled={sellingBusy}/></div>
            <div className="modal_body">
              
              <div className="modal_inside">

              <img src="/finger.png" alt="Sell" style={{ height: 80, marginBottom: 8 }} />
              

              <div className="shadow2" style={{ fontWeight: 600, fontSize: 24,marginBottom: 20 }}>
                {sellingCard.for_sale ? `Update Card “${sellingCard.name}”` : `Sell Card “${sellingCard.name}”`}
              </div>

              <form onSubmit={submitSell} style={{ width: "100%", display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ width: '100%', float: 'left' }}>
                  <div style={{ display: 'inline-block', position: 'relative' }}>
                    <img src="/runes.png" alt="$" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', height: 24, opacity: 0.9 }} />
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={sellPrice}
                      onChange={(e) => setSellPrice(clampSalePrice(e.target.value))}
                      className="form-control"
                      placeholder="Price"
                      style={{ height: 42, borderRadius: 8, border: 0, paddingLeft: 32, width: "100%",border: '2px solid #88482a',
                borderBottom: '5px solid #88482a', }}
                    />
                  </div>
                </div>
                              </form>

                <div style={{ width: '100%', textAlign: 'center', marginTop: 16 }}>
                  <div className="shadow1" style={{ fontSize: 16,fontWeight: 600}}>
                    Marketplace fee: <b>{feePct == null ? 'loading…' : `${feePct}%`}</b> (rounded up)<br/>Fee:&nbsp;
                    <b style={{ color: '#ffdf5f' }}>{feePreview}</b> Runes<br />You receive:&nbsp;
                    <b style={{ color: '#9adcfb' }}>{netPreview}</b> Runes<br/>
                    Max price allowed: <b>{MAX_SALE_PRICE}</b>
                  </div>
                
                </div>
                </div>

                <div className="modal_footer">
                  <button
  type="button"
  className="game-button green-button"
  disabled={sellingBusy}
  style={{ marginRight: 10 }}
  onClick={submitSell}
>
  {sellingBusy ? "Saving..." : sellingCard.for_sale ? "Update" : "Set Price"}
</button>

                  {sellingCard.for_sale && (
                    <button type="button" className="game-button yellow-button" style={{marginBottom: 10}} onClick={submitUnlist} disabled={sellingBusy}>
                      {sellingBusy ? 'REMOVE' : 'REMOVE'}
                    </button>
                  )}

                  <button className="game-button red-button" onClick={() => { setSellOpen(false); setSellingCard(null); setSellPrice(''); }} disabled={sellingBusy}>CLOSE</button>
                </div>

              
            </div>
          </motion.div>
        )}
      </AnimatePresence>

<AnimatePresence>
  {battleSoonOpen && (
    <motion.div
      className="modal-backdrop no-css-anim"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{ backdropFilter: 'blur(2px)' }}
    >
      <motion.div
        className="modal fancy-success-modal"
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 22 }}
        style={{ textAlign: 'center', position: 'relative', overflow: 'hidden' }}
      >
        {/* Sparkle ring */}
        {[...Array(18)].map((_, i) => (
          <motion.span
            key={i}
            initial={{ opacity: 0, scale: 0.4 }}
            animate={{
              opacity: [0, 1, 0],
              scale: [0.4, 1.2, 0.9],
              x: Math.cos((i / 18) * Math.PI * 2) * 160,
              y: Math.sin((i / 18) * Math.PI * 2) * 160
            }}
            transition={{ duration: 1.4, delay: 0.05 * i, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: 'linear-gradient(#ffdf5f,#9adcfb)',
              pointerEvents: 'none'
            }}
          />
        ))}

        <h1 className="pagetitle" style={{ color: '#ffdf5f', marginBottom: 6 }}>
          Ready to Rumble?
        </h1>

        {!limitInfo.loading && (
          <div style={{ marginTop: 2, marginBottom: 10 }}>
          <span
            style={{
              display: "inline-block",
              padding: "4px 10px",
              borderRadius: 999,
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: 0.5,
              background: "rgba(0,0,0,0.5)",
              border: "1px solid rgba(255,255,255,0.25)",
              color: "#ffdf5f",
              textShadow: "0 1px 2px #000",
            }}
          >
            {limitInfo.loading
              ? "Checking battles…"
              : limitReached
              ? `LIMIT REACHED ${limitInfo.used} / ${limitInfo.limit}${
                  cooldownLabel ? ` · next reset in ${cooldownLabel}` : ""
                }`
              : `DAILY BATTLE ${nextBattle} / ${limitInfo.limit}`}
          </span>
        </div>

)}


        <div
          style={{
            color: '#9adcfb',
            textShadow: '1px 1px #000',
            fontSize: 22,
            marginBottom: 6,
            marginTop: 12
          }}
        >
          {tagline}
        </div>

        <div style={{ fontSize: 20, color: '#ddd', opacity: 0.95, marginBottom: 16, lineHeight: 1.35 }}>
          {bulletLines.map((line, i) => (<div key={i}>{line}</div>))}
        </div>

        <div
          style={{
            fontSize: 18,
            color: '#ffdf5f',
            textShadow: '1px 1px #000',
            marginBottom: 12
          }}
        >
          {feeLoading ? 'Calculating payout…' : (
            <>
              Win Payout: <strong>{projectedWin}⚡</strong>
            </>
          )}
        </div>

        {/* ----- FOOTER: buttons on separate centered lines ----- */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="modal-footer"
          style={{ justifyContent: 'center', gap: 10, display: 'flex', flexWrap: 'wrap' }}
        >
          {/* Line 1: centered Enter button */}
          <div style={{ flexBasis: '100%', display: 'flex', justifyContent: 'center' }}>
            <SparkGuardButton
  ref={enterBtnRef}
  user={user}
  hintText="Not enough Sparks"
  disabled={limitReached}
  title={limitReached ? "Daily limit reached for this card" : undefined}
  style={{
    minWidth: 140,
    opacity: limitReached ? 0.7 : 1,
    cursor: limitReached ? "not-allowed" : "pointer",
  }}
  onConfirm={async () => {
    if (!pendingCard) return;

    if (limitReached) {
      setErrorMessage(`Daily battle limit reached (${limitInfo.used}/${limitInfo.limit}).`);
      setShowErrorModal(true);
      return;
    }

    const have = Number(user?.sparks ?? 0);
    const need = Number(entryFee ?? 0);
    if (Number.isFinite(need) && have < need) {
      setErrorMessage(`You need ${need} SPARKS to start, but you only have ${have}.`);
      setShowErrorModal(true);
      enterBtnRef.current?.showHint?.();
      return;
    }

    try {
      setEntering(true);

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/battle/bf8c3a/starta`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          'X-TG-INITDATA': window.Telegram?.WebApp?.initData || '',
        },
        body: JSON.stringify({ cardId: pendingCard.id }),
      });

      // NEW: handle daily limit enforced by backend
      if (res.status === 429) {
        const data = await res.json().catch(() => ({}));
        if (data?.error === "DAILY_LIMIT") {
          setErrorMessage(`Daily battle limit reached (${data?.used ?? "?"}/${data?.limit ?? "?"}).`);
          setShowErrorModal(true);
          return;
        }
      }

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
  const code = String(data?.error || "");
  const msg = code.toLowerCase();

  if (code === "DEFENSE_DECK_REQUIRED") {
    openDefenseDeckRequiredModal();
    return;
  }

  if (msg.includes("insufficient") || msg.includes("spark")) {
          setErrorMessage(data?.error || "Not enough SPARKS.");
          setShowErrorModal(true);
          enterBtnRef.current?.showHint?.();
          return;
        }

        setErrorMessage(data?.error || "Unable to start battle");
        setShowErrorModal(true);
        return;
      }

      // ✅ Update limit counter if start succeeds
      setLimitInfo((prev) => ({
        ...prev,
        used: Math.min(prev.used + 1, prev.limit),
      }));

      // Update user’s Sparks balance if provided
      if (data.user && typeof setUser === "function") {
        setUser((u) => ({ ...(u || {}), ...(data.user || {}) }));
      }

      // Close modal and navigate to battle
      setSelectedCardId?.(pendingCard.id);
      setBattleSoonOpen(false);

      posthog?.capture?.("battle_entered", {
        battle_id: data.battleId,
        card_id: pendingCard.id,
        card_name: pendingCard.name,
        card_rarity: pendingCard.rarity,
        entry_fee: entryFee,
        user_level: user?.level,
        opened_via: openedVia,
      });

      onNavigate("battle", { battleId: data.battleId, cardId: pendingCard.id });
    } catch (err) {
      const m = String(err?.message || "").toLowerCase();
      if (m.includes("insufficient") || m.includes("spark")) {
        enterBtnRef.current?.showHint?.();
      }
      setErrorMessage(err.message || "Transaction failed");
      setShowErrorModal(true);
    } finally {
      setEntering(false);
    }
  }}
>
  {entering
    ? "Summoning Arena…"
    : limitInfo.loading
      ? "Loading Battle Info…"
      : limitReached
        ? `Limit Reached (${limitInfo.used}/${limitInfo.limit})`
        : `Enter Battle ${nextBattle}/${limitInfo.limit} (${entryFee} Sparks)`}
</SparkGuardButton>

          </div>

          {/* Line 2: centered Not Today button */}
          <button
            onClick={() => {
              setBattleSoonOpen(false);
              setPendingCard(null);
              setFeeLoading(true);
            }}
            style={{
              minWidth: 120,
              marginTop: 0,
              alignSelf: 'center',
              flexBasis: '100%',   // break line
              maxWidth: 160,       // optional: cap width
              marginLeft: 'auto',  // center horizontally
              marginRight: 'auto',
            }}
          >
            Not Today 😅
          </button>
        </motion.div>
        {/* ----- /FOOTER ----- */}

        <div style={{ marginTop: 14, fontSize: 15, color: '#bbb', fontWeight: 'bold' }}>
          Pro tip: Evolve cards → bigger numbers → bigger flex.
        </div>
      </motion.div>
    </motion.div>
  )}
</AnimatePresence>

{abilityRefillOpen && (
  
  <motion.div className="modal_box" initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}>

      <div className="modal_title">Limit Reached
       <button
        className="modal_close"
          onClick={() => {
            setAbilityRefillOpen(false);
            setAbilityTarget(null);
          }}
          disabled={abilityRefillBusy}
        /></div>
      
    
    <div className="modal_body">
      
      <div className="modal_inside">

      <div className="shadow2" style={{fontWeight: 600, fontSize: 28}}>
        Reset Ability Limit?
      </div>

      <img
        src="/runes.png"
        alt="Runes"
        style={{ width: 56, height: 56, marginBottom: 10, marginTop: 10 }}
      />

      <div className="shadow1"
        style={{
          fontWeight: 600,fontSize: 18,marginBottom: 10
        }}
      >
        You reached the limit for ability changes.
        <div className="info" style={{fongWeight: 600, fontSize: 21}}>
        Cost:{" "}
        <img src="/runes.png" style={{verticalAlign: "middle", height: 24}}/> {Number(abilityChangeInfo.refillPriceRunes ?? 0)}</div>
      </div>

      </div>

      <div className="modal_footer">
        <button
          onClick={resetAbilityLimit}
          disabled={abilityRefillBusy}
          className="game-button green-button"
          style={{marginRight: 10}}
        >
          {abilityRefillBusy
            ? "Resetting..."
            : `Confirm`}
        </button>

        <button
        className="game-button red-button"
          onClick={() => {
            setAbilityRefillOpen(false);
            setAbilityTarget(null);
          }}
          disabled={abilityRefillBusy}
        >
          CANCEL
        </button>
      </div>
        </div>
    </motion.div>
)}

{abilityChangeOpen && abilityTarget && (
  <motion.div className="modal_box" initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      >

      <div className="modal_title">Ability Change

<button
        className="modal_close"
          onClick={() => {
            setAbilityChangeOpen(false);
            setAbilityTarget(null);
          }}
          disabled={abilityChangeBusy}
        />

      </div>
    
    <div
      className="modal_body"
      
    >
    <div className="modal_inside">

     <div className="shadow2" style={{fontWeight: 600, fontSize: 28, marginBottom: 10}}>
        Change {abilityTarget}?
      </div>

      <img
        src={`/a_${String(abilityTarget).toLowerCase()}.png`}
        alt={abilityTarget}
        style={{height: 80, marginBottom: 5 }}
      />

     

      <div className="shadow1" style={{fontWeight: 600,fontSize: 18}}>
        This will replace <b>{abilityTarget}</b> with a random ability this card does not already have.
      </div>

      <div
  style={{
    marginTop: 12,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
  }}
>
  <div
    className="shadow1" style={{fontWeight: 600, fontSize: 18}}>
    {abilityChangeInfo.limitMode === "overall" ? "Used" : "Used today"}:{" "}
    {abilityChangeInfo.loading ? "..." : abilityChangeInfo.used ?? "—"}/{abilityChangeInfo.limit ?? "—"}
  </div>

  <div className="info" style={{fontSize: 21}}>
    
      Cost: 
    

    
      <img
        src={abilityChangeCurrency === 'runes' ? '/runes.png' : '/sparks.png'}
        alt={abilityChangeCurrency || 'currency'}
        style={{
          width: 26,
          marginLeft: 5,
          height: 26,
          verticalAlign: 'middle'
        }}
      />
      {abilityChangePrice ?? '—'}
    
  </div>
</div>
</div>
      <div className="modal_footer">
        <button
        className="game-button green-button" style={{marginRight: 10}}
          onClick={submitAbilityChange}
          disabled={
            abilityChangeBusy ||
            abilityChangeInfo.loading ||
            abilityChangePrice == null ||
            abilityChangePrice <= 0
          }
        >
          {abilityChangeBusy ? 'Changing...' : 'Confirm'}
        </button>

        <button
        className="game-button red-button"
          onClick={() => {
            setAbilityChangeOpen(false);
            setAbilityTarget(null);
          }}
          disabled={abilityChangeBusy}
        >
          CANCEL
        </button>
      </div>
      </div>
    </motion.div>
  
)}

<AnimatePresence>
  {defenseDeckRequiredOpen && (
    <div className="modal-backdrop no-css-anim">
      <motion.div
        className="modal fancy-success-modal"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        style={{ textAlign: "center" }}
      >
        <img
          src="/shield.png"
          alt="Defense Deck"
          style={{
            height: 76,
            marginBottom: 10,
            filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.45))",
          }}
          onError={(e) => {
            e.currentTarget.src = "/finger.png";
          }}
        />

        <h2
          style={{
            color: "#ffdf5f",
            marginBottom: 10,
            textShadow: "0 1px 2px #000",
          }}
        >
          Defense Deck Required
        </h2>

        <p
          style={{
            textShadow: "1px 1px #000",
            fontSize: 22,
            color: "#ddd",
            lineHeight: "28px",
            marginBottom: 10,
          }}
        >
          You need to set your Defense Deck before starting a battle.
        </p>

        <div
          className="modal-footer"
          style={{
            marginTop: 20,
            display: "flex",
            justifyContent: "center",
            gap: 3,
            flexWrap: "wrap",
          }}
        >
          <button onClick={() => setDefenseDeckRequiredOpen(false)}>
            Later
          </button>

          <button onClick={goToDefenseDeck}>
            Defense Deck
          </button>
        </div>
      </motion.div>
    </div>
  )}
</AnimatePresence>

      {/* RENAME CARD MODAL */}
      {renameOpen && selectedCard && (
        <motion.div className="modal_box" initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            >

        <div className="modal_title">
              Rename This Card?
              <button
                onClick={() => {
                  setRenameOpen(false);
                }}
              className="modal_close"/>
            </div>

          <div className="modal_body">

            <div className="modal_inside">

            <img
              src="/finger.png"
              alt="Rename"
              style={{ height: 80, marginBottom: 10 }}
            />

            <div className="shadow2" style={{marginBottom: 20, fontSize: 21, fontWeight: 600}}>Change Card Name?</div>
            
            <input
              type="text"
              maxLength={32}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              style={{
                width: '100%',
                margin: '0 auto',
                display: 'block',
                padding: '16px 10px',
                boxSizing: 'border-box',
                borderRadius: 8,
                border: '2px solid #88482a',
                borderBottom: '5px solid #88482a',
                background: '#fff',
                color: '#000',
                textAlign: 'center',
                fontWeight: 600,
                fontSize: 18
              }}
              placeholder="Enter new name"
            />
            <div className="shadow1" style={{marginTop: 10, marginBottom: 10, fontSize: 14, fontWeight: 600, marginTop: 10}}>Cards with offensive names will be deleted.</div>

            <div className="info" style={{fontWeight: 600, fontSize: 21}}>
                Cost: <span style={{color: "#f3e14d"}}><img src="/sparks.png" style={{height: 21, verticalAlign: "middle"}}/>{nameChangePrice}</span>
            
            </div>

            </div>

            <div className="modal_footer">
              <button className="game-button green-button" style={{marginRight: 10}} onClick={submitRename} disabled={renameBusy}>
                {renameBusy
                  ? 'Renaming...'
                  : `Confirm`}
              </button>

               <button
                onClick={() => {
                  setRenameOpen(false);
                }}
              className="game-button red-button">CLOSE</button>
              
            </div>
          </div>
        </motion.div>
      )}

      {pieceConvertSuccess && (
  <div className="modal-backdrop">
    <motion.div
      className="modal fancy-success-modal"
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
    >
      <img
        src="/sparks.png"
        alt="Sparks"
        style={{ height: "72px", marginBottom: "10px" }}
      />

      <h2 style={{ color: "#ffdf5f", marginBottom: "10px" }}>
        Pieces Converted!
      </h2>

      <div
        style={{
          color: "#fff",
          fontSize: 22,
          lineHeight: "30px",
          textAlign: "center",
          textShadow: "1px 1px #000",
          marginBottom: 16,
        }}
      >
        Converted{" "}
        <span style={{ color: "#ffdf5f", fontWeight: 800 }}>
          {pieceConvertSuccess.piecesConverted}
        </span>{" "}
        card pieces into{" "}
        <span style={{ color: "#F8BA1E", fontWeight: 800 }}>
          {pieceConvertSuccess.sparksAwarded}
        </span>{" "}
        Sparks!
      </div>

      <div
  className="modal-footer"
  style={{
    display: "flex",
    gap: 8,
    justifyContent: "center",
    width: "100%",
  }}
>
  <button
    type="button"
    onClick={() => setPieceConvertSuccess(null)}
    style={{ minWidth: 130 }}
  >
    Nice!
  </button>
</div>
    </motion.div>
  </div>
)}


      {/* ERROR MODAL */}
      {showErrorModal && (
        <motion.div className="modal_box" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>
        <div className="modal_title">OOPS!
        <button className="modal_close" onClick={() => { setShowErrorModal(false); setErrorMessage(''); }}/></div>
          <div className="modal_body">
            <div className="modal_inside">
            
            <img src="/finger.png" alt="Error" style={{ height: 80, marginBottom: 10, transform: 'rotate(180deg)' }} />
            <div className="shadow2" style={{fontWeight: 600, fontSize: 28, marginBottom: 10 }}>Transaction Failed</div>
            <div className="shadow1"style={{ fontWeight: 600, fontSize: 18, TextTransform: 'capitalize'}}>
              {errorMessage}
            </div>
            </div>
            
            <div className="modal_footer">
              <button className="game-button red-button" onClick={() => { setShowErrorModal(false); setErrorMessage(''); }}>
                CLOSE
              </button>
            </div>
        </div>
        </motion.div>
      )}
    </motion.div>
  );
}

export default Character;
